<template>
  <div class="col-sm-12">
    <el-table class="wrap-whole-words table-striped"
              :id="tableId()"
              :key="tableKey"
              :data="sortedTableData"
              :row-class-name="maybeHighlightJob"
              :header-cell-class-name="headerSortClass"
              :show-header=true
              ref="jobsTable"
              style="width: 100%">
      <el-table-column v-for="column in filteredTableColumns"
                       :min-width="column.minWidth"
                       :key="column.label"
                       :label="column.label">
        <template v-slot="props">
          <component :is="column.component" :data="getColumnData(column, props.row)" />
        </template>
      </el-table-column>
      <el-table-column label="Action" fixed="right" :width="actionColumnWidth">
        <template v-slot="props">
          <template v-if="isSuperUser">
            <a v-if="hasCamundaIncidents(props.row)" @click.prevent="viewCamundaIncidents(props.row.camundaIncidents)" href="#">
              <img src="static/img/icon-camunda-incident.png" title="Camunda Incidents" />
            </a>
            <img v-else src="static/img/transparent.png" style="width: 27px;" />
          </template>
          <a @click.prevent="viewLog(props.row.id)" href="#"><img src="static/img/icon-log.png" title="Job Error Log" class="m-left-6" /></a>
          <a @click.prevent="viewFiles(props.row)" v-if="canReadFiles" href="#"><img src="static/img/icon-folder.png" title="Job Files" class="m-left-8" /></a>
          <a @click.prevent="viewBulkEditTasks(props.row.id)" href="#"><img src="static/img/icon-tasks-edit.png" title="Bulk Edit Tasks" class="m-left-8" /></a>
          <a @click.prevent="viewTaskSearchResults(props.row.id)" href="#"><img src="static/img/icon-tasks.png" title="Job Tasks" class="m-left-8" /></a>
          <a @click.prevent="viewJobProgress(props.row)" href="#" v-if="props.row.processInstanceId"><img src="static/img/icon-bpmn.png" title="Job Progress & Tasks" class="m-left-8" /></a>
          <a @click.prevent="downloadProcessDefinition(props.row.processDefinitionId)" href="#" v-if="props.row.processDefinitionId && canReadCamunda"><img src="static/img/icon-download.png" title="Download Process Definition (xml)" class="m-left-8" /></a>
          <div class="relative" style="display: inline-block"><a @click.prevent="openComments(props.row)" href="#"><img :id="'_' + props.row.id" :src="iconColour(getCommentCount(getParentEntityIdHash(props.row)))" :ref="getParentEntityIdHash(props.row)" title="Job Comments" class="m-left-8" /><div class="button-count button-count-department" v-if="getCommentCount(getParentEntityIdHash(props.row)) > 0" >{{ getCommentCount(getParentEntityIdHash(props.row)) }}</div></a></div>
          <a @click.prevent="confirmDelete(props.row)" href="#" v-if="canEdit"><img src="static/img/icon-delete.png" title="Delete Job" class="m-left-8" /></a>
          <a @click.prevent="refreshProjectJobs(props.row)" href="#"><img src="static/img/icon-refresh.png" title="Refresh Job" class="m-left-8" /></a>
          <span v-if="!isJobCompleted(props.row)">
            <a @click.prevent="replaceJob(props.row)" href="#" v-if="canEdit"><img src="static/img/icon-pencil.png" title="Change Job Type" class="m-left-8" /></a>
            <a @click.prevent="confirmTerminate(props.row)" href="#" v-if="canTerminateProcessInstance"><img src="static/img/icon-bpmn-error.png" title="Terminate Process Instance, Delete Tasks, and Complete Job" class="m-left-8" /></a>
          </span>
         </template>
      </el-table-column>
    </el-table>
    <div class="row">
      <div class="col-md-12" style="display: flex; justify-content: end; align-items: center">
        <span v-if="projectWorkStopped" class="warning-row" style="padding: 7px 10px; border-radius: 3px;">Work has stopped on this project, no Jobs can be added.</span>
        <button v-if="canAdd" type="button" :disabled="projectWorkStopped" @click="addJob" class="btn btn-fill btn-info btn-wd btn-add-job pull-right">Add Job</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fileSystemPath,
  notifyError,
  notifyWarning,
  notifySuccess,
  getParentEntityIdProjectDepartmentJobHash,
  tableHeaderSortCssClass,
  sortNested,
  filterAndSortTableColumns,
  assembleTableColumnSortProps
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import {NO_CONTENT, OK, UNAUTHORIZED} from 'http-status-codes'
import lodash from 'lodash'
import {confirmDeleteJob, refreshJobs} from 'src/utilities/jobHelper'
import tableColumns from '@/utilities/tableColumns'

export default {
  props: [
    'projectId',
    'department'
  ],
  computed: {
    isSuperUser() {
      return this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)
    },
    user() {
      return this.$store.get('security/user')
    },
    sortedTableData() {
      return this.jobs.sort(sortNested(this.sortedProps))
    },
    filteredTableColumns() {
      return filterAndSortTableColumns(this.tableColumns, this.user, Constants.USER_PREFERENCE_TYPES.JOBS_BY_DEPARTMENT)
    },
    sortedProps() {
      return assembleTableColumnSortProps(this.filteredTableColumns)
    },
    actionColumnWidth() {
      if (this.$store.get('security/isInGroup', Constants.GROUPS.STANDARD_USERS)
        || this.$store.get('security/isInGroup', Constants.GROUPS.ADVANCED_USERS)) {
        return 280 // column width hack
      }
      if (this.$store.get('security/isInGroup', Constants.GROUPS.POWER_USERS)) {
        return 340 // column width hack
      }
      if (this.$store.get('security/isInGroup', Constants.GROUPS.ADMIN_USERS)) {
        return 370 // column width hack
      }
      return 435 // column width hack
    },
    jobs() {
      return this.$store.getters['jobs/byDepartment'](this.department.id)
    },
    canAdd() {
      return this.$can('create', Permissions.JOBS)
    },
    canEdit() {
      return this.$can('edit', Permissions.JOBS)
    },
    canTerminateProcessInstance() {
      return this.$can('edit', Permissions.JOB_TERMINATE_PROCESS_INSTANCE)
    },
    canReadFiles() {
      return this.$can('read', Permissions.FILES)
    },
    canReadCamunda() {
      return this.$can('read', Permissions.CAMUNDA)
    },
    projectWorkStopped() {
      return this.$store.getters['projects/edit/details/stopWork']
    }
  },
  data() {
    return {
      lodash,
      tableKey: 0,
      toDelete: null,
      tableColumns: tableColumns.PROJECT_DEPARTMENT
    }
  },
  methods: {
    headerSortClass({columnIndex}) {
      return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, 0)
    },
    getColumnData(column, row) {
      if (row.coordinatorId) {
        row.coordinatorName = this.$store.get('users/displayName', row.coordinatorId)
      }
      return column.prop ? lodash.get(row, column.prop) : row
    },
    iconColour(commentCount) {
      return commentCount > 0 ? 'static/img/comment-red.png' : 'static/img/comment-blue.png'
    },
    getCommentCount(parentEntityIdHash) {
      return this.$store.getters['comments/count'](parentEntityIdHash)
    },
    isJobCompleted(job) {
      return Constants.STATUSES.COMPLETED === job.status || Constants.STATUSES.CANCELLED === job.status
    },
    viewFiles(job) {
      const projectId = this.$store.getters['projects/edit/projectId']
      const projectName = this.$store.getters['projects/edit/details/name']
      const path = fileSystemPath(projectName, projectId, this.department.name, job.name, job.id)
      this.$store.commit('files/jobId', job.id)
      this.$store.commit('files/jobPath', path)
      this.$eventHub.$emit('show-modal-1', 'File Browser', path, '800px')
    },
    viewBulkEditTasks(jobId) {
      this.$eventHub.$emit('show-modal-2', 'Bulk Edit Tasks', {jobId}, '95%')
    },
    viewTaskSearchResults(jobId) {
      this.$store.commit('tasks/search/resetQuery')
      this.$store.commit('tasks/search/resetResults')
      this.$router.push({path: `/tasks/search?jobId=${jobId}`})
    },
    hasCamundaIncidents(job) {
      return !!job.camundaIncidents
    },
    viewCamundaIncidents(incidents) {
      this.$eventHub.$emit('show-modal-1', 'Camunda Incidents', incidents, '600px')
    },
    viewLog(jobId) {
      this.$eventHub.$emit('show-modal-1', 'View Log', {entityType: 'Job', entityId: jobId}, '80%')
    },
    viewJobProgress(job) {
      this.$eventHub.$emit('show-modal-1', 'Job Progress', job, '80%')
    },
    async downloadProcessDefinition(processDefinitionId) {
      const response = await this.$store.dispatch('camunda/fetchProcessDefinitionXml', processDefinitionId)

      if (response.result === Constants.RETURN_CODES.ERROR) {
        notifyError(this, "Unable to Download BPMN XML")
        return
      }

      await this.$store.dispatch('camunda/fetchAndDownloadProcessDefinition', processDefinitionId)
    },
    refreshProjectJobs(job) {
      refreshJobs(this, job)
    },
    replaceJob(job) {
      this.$eventHub.$emit('show-modal-1', 'Replace Job', job, '700px')
    },
    addJob() {
      this.$store.commit('jobs/resetCreate')
      this.$store.set('jobs/create@departmentId', this.department.id)
      this.$store.set('jobs/create@projectId', this.projectId)
      this.$eventHub.$emit('show-modal-1', 'Create Job Wizard', null, '95%')
    },
    confirmDelete(job) {
      confirmDeleteJob(this, job)
    },
    showConfirmDelete(job, message, showButton) {
      const payload = {
        message,
        entity: job,
        delete: this.delete,
        showButton
      }
      this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
    },
    async delete() {
      const response = await this.$store.dispatch('delete', {endpoint: 'jobs', id: this.toDelete})
      if (NO_CONTENT === response.status) {
        notifySuccess(this, 'Job Deleted Successfully')
        this.$eventHub.$emit('jobDeleted')
      } else if (UNAUTHORIZED === response.status) {
        notifyWarning(this, 'You are not authorized to delete Jobs')
      } else {
        notifyError(this, 'Error Deleting Job')
      }
    },
    confirmTerminate(job) {
      this.$eventHub.$emit('show-modal-1', 'Terminate Camunda Process', job, '1000px')
    },
    getParentEntityIdHash(job) {
      return getParentEntityIdProjectDepartmentJobHash(this.projectId, job.departmentId, job.id)
    },
    getParentEntityId(job) {
      return {
        projectId: this.projectId,
        departmentId: job.departmentId,
        jobId: job.id
      }
    },
    openComments(job) {
      const projectName = this.$store.getters['projects/edit/details/name']
      const payload = {
        parentId: this.getParentEntityId(job),
        subheading: `<strong>Project:</strong> ${projectName}<br/><strong>Department: </strong>${this.department.name}<br/><strong>Job:</strong> ${job.name}`
      }
      this.$eventHub.$emit('show-modal-1', 'Comments', payload)
    },
    tableId() {
      return 'job-table-' + this.department.id
    },
    watchForOpenCommentsQuerystring() {
      setTimeout(() => {
        const qs = this.$route.query
        if (qs && qs.hasOwnProperty('cp') && this.$refs.hasOwnProperty(qs.cp)) {
          let jobId = this.$refs[qs.cp].id
          if (jobId) {
            jobId = jobId.substr(1, jobId.length) // trim off leading underscore
            this.openComments(this.$store.getters['jobs/byId'](jobId))
          }
        }
      }, Constants.TIME.MILLIS.ONE_SECOND)
    },
    maybeHighlightJob(props) {
      const qs = this.$route.query
      if (qs && qs.hasOwnProperty('jobId')) {
        if (props.row.id === qs.jobId) {
          return 'background-notice'
        }
      } else if (props.row.status === Constants.STATUSES.ERROR) {
        return 'background-error'
      }
    },
    redoLayout() {
      setTimeout(() => {
        if (this.$refs.jobsTable) {
          this.$refs.jobsTable.doLayout()
        }
      }, Constants.TIME.MILLIS.TENTH_SECOND)
    }
  },
  mounted() {
    this.$eventHub.$on('projectJobsLoaded', this.watchForOpenCommentsQuerystring)
    this.$eventHub.$on('jobsExpanded', this.redoLayout)
  },
  destroyed() {
    this.$eventHub.$off('projectJobsLoaded')
    this.$eventHub.$off('jobsExpanded')
  },
  watch: {
    '$route.query' (qs) {
      this.watchForOpenCommentsQuerystring()
    },
    filteredTableColumns: function() {
      this.tableKey++
    }
  }
}
</script>

<style scoped>
  .btn-add-job {
    margin-right: 12px;
  }
  .table-striped .invisible {
    display: none;
  }
  .button-count-department {
    position: absolute;
    right: -10px;
    bottom: 12px;
    height: 12px;
  }
  >>> .el-table__body .el-table__row.background-notice {
     background-color: lightpink;
  }
  >>> .el-table__body .el-table__row.background-error {
    background-color: lightcoral;
    color: black;
  }
</style>
